const { Logger, html } = window;
const { isAuthenticated, hasLinkedCards } = window.OverDrive;

import '../../util/Promise';
import ErrorTemplate from '../../components/common/templates/Error.html';

import ProcessForwardedAction from '../../util/title-actions/processForwardedAction';
import HashManager from '../../components/common/HashManager';
import TitleCollection from '../media/models/TitleCollection';

import PageManager from '../collection_campaign_page/PageManager';
import CollectionManager from '../collection_campaign_page/CollectionManager';
import CollectionHighlightView from '../collection_highlight/views/CollectionHighlight';
import CollectionHighlightFactory from '../collection_campaign_page/CollectionHighlightFactory';

import PatronTitleStatus from '../../models/PatronTitleStatus';

export default class FeaturedCollectionView extends Backbone.View {
    constructor({ viewName, room, bus, featureManager, patronSettings }) {
        super(...arguments);

        this._views = [];
        this.viewName = _.unescape(viewName.replace(/&#39;/g, "'"));
        this.room = room;
        this.bus = bus;
        this.featureManager = featureManager;
        this.patronSettings = patronSettings;
        this.pageSize = 10;

        this.pageManager = new PageManager();
        this.collectionManager = new CollectionManager();

        this.collectionErrors = [];

        this.update();
    }

    update() {
        this.$el.empty();

        this._views.forEach((view) => view.remove());
        this._views = [];

        this.pageManager
            .fetchRoomById((this.room && this.room.id) || 'home')
            .then(() => {
                this._views = this.initViews();
                this.unrenderedViews = this._views.slice();
                this._renderPage();
                this._performForwardedAction();
            });
    }

    _renderPage() {
        this._fetchRows(this.pageSize).then((collections) => {
            this._updatePatronTitleStatus(collections);
            this._pageLoadComplete();
        });
    }

    _fetchRows(rowCount, collectionsDone = []) {
        let views = this.unrenderedViews.splice(0, rowCount);
        this._paintCollections(views);

        return new Promise((resolve) => {
            this._syncRows(views, collectionsDone).then((result) => {
                if (result.failed) {
                    this._fetchRows(result.failed, collectionsDone).then(
                        (collections) => {
                            resolve(collections);
                        }
                    );
                } else {
                    resolve(collectionsDone);
                }
            });
        });
    }

    _syncRows(views, collections) {
        let requestQueue = views.map((view) => view.sync());

        return Promise.settle(requestQueue).then((results) => {
            const succeeded = results.filter(
                (result) => result.fulfilled && result.value
            );
            const failed = results.filter(
                (result) => !result.fulfilled || !result.value
            );

            succeeded.map((request) => collections.push(request.value));

            return { failed: failed.length };
        });
    }

    _paintCollections(views) {
        let frag = document.createDocumentFragment();

        views.forEach((view) => {
            if (!view.isPainted) {
                frag.appendChild(view.el);
                view.isPainted = true;
            }
        });

        this.el.appendChild(frag);
    }

    _pageLoadComplete() {
        if (this.unrenderedViews.length > 0) {
            let $loadMore = $(
                `<button class="center u-allCaps button secondary radius" aria-label="${html(
                    'loadMoreCollections'
                )}">${html('loadMore')}</button>`
            ).on('click', () => {
                this._renderPage();
                $loadMore.remove();
            });

            this.$el.append($loadMore);
        }

        if (this.collectionErrors.length > 0) {
            let allCollectionsFailed = this._views.length === 0;
            this._logPageErrors(this.collectionErrors, allCollectionsFailed);
            if (allCollectionsFailed) {
                this.$el.empty().append(ErrorTemplate());
            }
            this.collectionErrors = [];
        }
    }

    _logPageErrors(failed, allFailed) {
        const howMany = allFailed ? 'Total' : 'Partial';
        let errors = [];
        for (let failure of failed) {
            errors.push({
                id: failure.id,
                type: failure.type,
                statusText: failure.statusText,
                status: failure.status,
            });
        }
        let counts = _.countBy(
            errors,
            (error) => error.type + ':' + error.statusText
        );
        Logger.log('error', `${howMany} Featured Page Ajax Error`, {
            message: 'HighlightErrorRollup',
            errors: errors,
            counts: counts,
            page: this.viewName,
        });
    }

    initViews() {
        let views = [];
        let mainView = this.pageManager.getView(this.viewName);

        if (this._shouldTruncateCollections) {
            mainView.collectionDefinitions =
                mainView.collectionDefinitions.slice(0, 10);
        }

        this.collectionManager.reset(mainView.collectionDefinitions);

        this.collectionManager.each((collectionDefinition) => {
            // TitleCard template will render in "dummy" state
            collectionDefinition.titleCollection.reset([
                { placeholder: true },
                { placeholder: true },
                { placeholder: true },
                { placeholder: true },
                { placeholder: true },
                { placeholder: true },
            ]);

            let collectionHighlightView = CollectionHighlightFactory.Build(
                collectionDefinition,
                {
                    model: collectionDefinition,
                    bus: this.bus,
                    featureManager: this.featureManager,
                    room: this.room,
                }
            );

            views.push(collectionHighlightView.render());

            collectionHighlightView.sync = () =>
                collectionDefinition
                    .fetchCollectionTitles({
                        patronSettings: this.patronSettings,
                        room: this.room,
                    })
                    .then(
                        () => {
                            if (collectionDefinition.titleCollection.length) {
                                collectionHighlightView.render();
                                collectionHighlightView.toggleTitleVisibility(
                                    true
                                );

                                return collectionDefinition;
                            } else {
                                // Clean up the failed row
                                collectionHighlightView.remove();
                                this._views = this._views.filter(
                                    (v) => v !== collectionHighlightView
                                );
                                throw new Error(
                                    'No collection contents retrieved'
                                );
                            }
                        },
                        (error) => {
                            collectionHighlightView.remove();
                            this._views = this._views.filter(
                                (v) => v !== collectionHighlightView
                            );

                            error.id = collectionDefinition.id;
                            error.statusText = !!error.statusText
                                ? error.statusText
                                : 'UnknownStatus';
                            error.type = 'Ajax Error: Collection';

                            this.collectionErrors.push(error);

                            throw error;
                        }
                    );
        });

        return views;
    }

    _updatePatronTitleStatus(views) {
        if (
            window.OverDrive.isAuthenticated &&
            window.OverDrive.hasLinkedCards &&
            views &&
            views.length > 0
        ) {
            let titles = [];

            views.forEach((view) => {
                titles.push(...view.titleCollection.models);
            });

            let patronTitleStatus = new PatronTitleStatus();

            patronTitleStatus.fetchTitleStatus(titles).then(() => {
                patronTitleStatus.updateTitleCollectionState(_(titles));
            });
        }
    }

    _performForwardedAction() {
        if (isAuthenticated && hasLinkedCards && HashManager.get('action')) {
            let titles = [];
            this.collectionManager.each((collectionDefinition) => {
                titles.push(...collectionDefinition.titleCollection.models);
            });
            const forwardedAction = new ProcessForwardedAction(
                new TitleCollection(titles),
                this.bus
            );
            forwardedAction.executeAction();
            window.forwardedAction = forwardedAction;
        }
    }

    get _shouldTruncateCollections() {
        // The OD app on iOS 8 and 9 crashes on the featured collection page if there are too many collections.  LHTNG-2387
        let inApp = Cookies.get('omcbrowse') !== undefined;
        if (inApp && bowser.ios && bowser.osversion) {
            let versionParts = bowser.osversion.split('.');
            if (versionParts && versionParts.length > 0) {
                let majorVersion = parseInt(versionParts[0], 10);
                if (majorVersion && majorVersion < 10) {
                    return true;
                }
            }
        }
        return false;
    }
}
